import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Share2, ShieldCheck, Loader2, X, Plus, Ban } from 'lucide-react';
import { sw, P } from '../../lib/sitewalkApi';

/** Contractor sharing governance UI (grant/revoke). Deny-by-default; backend is authoritative. */
export default function SharingManager({ projectId }) {
  const [walks, setWalks] = useState([]);
  const [parties, setParties] = useState([]);
  const [sel, setSel] = useState(null);          // selected walk id
  const [shareInfo, setShareInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [partyId, setPartyId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [w, p] = await Promise.all([
        sw.walks(projectId, { pageSize: 200 }),
        axios.get(`${P(projectId)}/parties`).then((r) => r.data).catch(() => ({ items: [] })),
      ]);
      setWalks(w.items || []);
      setParties(p.items || p.parties || []);
    } catch (e) { toast.error('Failed to load'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const openShares = async (wid) => {
    setSel(wid); setShareInfo(null);
    try { setShareInfo(await sw.shares(projectId, wid)); } catch (e) { toast.error('Cannot load shares'); }
  };

  const grant = async () => {
    if (!partyId) return toast.error('Select a contractor organization');
    setBusy(true);
    try { await sw.share(projectId, sel, { partyIds: [partyId] }); toast.success('Access granted'); setShareInfo(await sw.shares(projectId, sel)); setPartyId(''); }
    catch (e) { toast.error(e.response?.data?.detail || 'Grant failed'); }
    setBusy(false);
  };
  const revoke = async (pid) => {
    setBusy(true);
    try { await sw.revoke(projectId, sel, { partyIds: [pid] }); toast.success('Access revoked'); setShareInfo(await sw.shares(projectId, sel)); }
    catch (e) { toast.error(e.response?.data?.detail || 'Revoke failed'); }
    setBusy(false);
  };

  const partyName = (id) => (parties.find((p) => p.id === id)?.name) || id;
  const sharedParties = shareInfo?.sharedWith?.partyIds || [];
  const grantable = parties.filter((p) => !sharedParties.includes(p.id));

  return (
    <div data-testid="sharing-manager">
      <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
        <ShieldCheck className="w-4 h-4 text-emerald-600" /> Deny-by-default. Contractors see a walk only when explicitly granted. Backend enforces access — hiding is not security.
      </div>
      {loading ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="grid md:grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-2 max-h-[70vh] overflow-y-auto" data-testid="sharing-walk-list">
            {walks.map((w) => (
              <button key={w.id} onClick={() => openShares(w.id)} className={`w-full text-left px-3 py-2 rounded-lg text-sm ${sel === w.id ? 'bg-sky-50 border border-sky-300' : 'hover:bg-slate-50 border border-transparent'}`} data-testid={`sharing-walk-${w.walkNumber}`}>
                <div className="font-medium text-slate-700">{w.walkNumber}</div>
                <div className="text-[11px] text-slate-400">{w.name} · shared with {(w.sharedWith?.partyIds || []).length} org(s)</div>
              </button>
            ))}
            {walks.length === 0 && <p className="text-xs text-slate-400 p-2">No site walks.</p>}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="sharing-detail">
            {!sel ? <p className="text-sm text-slate-400">Select a site walk to manage contractor access.</p> : !shareInfo ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
              <>
                <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2"><Share2 className="w-4 h-4 text-sky-600" /> {shareInfo.walkNumber}</h4>
                <div className="flex gap-2 mb-3">
                  <select value={partyId} onChange={(e) => setPartyId(e.target.value)} className="flex-1 border rounded-lg px-2 py-1.5 text-xs" data-testid="sharing-party-select">
                    <option value="">Select contractor organization…</option>
                    {grantable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <button onClick={grant} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 bg-sky-600 text-white rounded-lg text-xs disabled:opacity-50" data-testid="sharing-grant-btn"><Plus className="w-3.5 h-3.5" /> Grant</button>
                </div>
                <div className="text-xs font-medium text-slate-500 mb-1">Current access</div>
                <div className="space-y-1.5" data-testid="sharing-current-list">
                  {sharedParties.length === 0 && <p className="text-xs text-slate-400">No contractor organizations have access.</p>}
                  {sharedParties.map((pid) => (
                    <div key={pid} className="flex items-center justify-between rounded-lg border border-slate-100 px-2.5 py-1.5" data-testid={`sharing-grant-${pid}`}>
                      <span className="text-xs text-slate-700">{partyName(pid)}</span>
                      <button onClick={() => revoke(pid)} disabled={busy} className="flex items-center gap-1 text-[11px] text-red-600 hover:text-red-700" data-testid={`sharing-revoke-${pid}`}><Ban className="w-3.5 h-3.5" /> Revoke</button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
